import React, { useId } from 'react';

// Tappable front + back anatomy figure. Same geometry as the Flutter app's
// lib/widgets/body_anatomy_map.dart: a 215 × 200 unit space with the front
// figure at x 0–100 and the back figure at x 115–215, each drawn around its
// own x = 50. Region keys match the `focus` / `limitations` values that
// fitviz_cloud gpt_program.py accepts.

export const FOCUS_REGIONS = ['chest', 'back', 'shoulders', 'arms', 'legs', 'glutes', 'core'];
export const INJURY_REGIONS = ['neck', 'shoulder', 'elbow', 'wrist', 'lower_back', 'hip', 'knee', 'ankle'];

const W = 215;
const H = 200;
const LABEL_SPACE = 14;
const BACK = 115;

const n = (v) => Math.round(v * 100) / 100;
const id = (x) => x;
const mirror = (x) => 100 - x;

/** Tiny SVG path builder; every x goes through [x] so shapes can be mirrored. */
function path(x, steps) {
  return steps
    .map(([cmd, ...v]) => {
      if (cmd === 'Z') return 'Z';
      const pts = [];
      for (let i = 0; i < v.length; i += 2) pts.push(`${n(x(v[i]))} ${n(v[i + 1])}`);
      return `${cmd}${pts.join(' ')}`;
    })
    .join(' ');
}

const pair = (steps) => [path(id, steps), path(mirror, steps)];

function oval(cx, cy, w, h) {
  const rx = w / 2;
  const ry = h / 2;
  return `M${n(cx - rx)} ${cy} A${rx} ${ry} 0 1 0 ${n(cx + rx)} ${cy} A${rx} ${ry} 0 1 0 ${n(cx - rx)} ${cy} Z`;
}

const ovalPair = (cx, cy, w, h) => [oval(cx, cy, w, h), oval(100 - cx, cy, w, h)];

function rrect(l, t, r, b, rad) {
  return `M${l + rad} ${t} H${r - rad} Q${r} ${t} ${r} ${t + rad} V${b - rad} Q${r} ${b} ${r - rad} ${b} H${l + rad} Q${l} ${b} ${l} ${b - rad} V${t + rad} Q${l} ${t} ${l + rad} ${t} Z`;
}

/** Whole-body outline: down the left side, back up the mirrored right side. */
function silhouette() {
  const pts = [
    [42.8, 3.2, 42.4, 12.5], [42.6, 20, 45.3, 24.5],
    [46, 27.5, 45.6, 30], [42, 32, 37, 33.5], [28.5, 35.5, 25.6, 43.5],
    [23.6, 50, 23.8, 57], [23.5, 62, 22.6, 66.5], [19.8, 73.5, 19.6, 79], [19, 85, 18.3, 88.5],
    [15.6, 92, 16.2, 98.5], [17, 103.5, 19.6, 102.8], [22, 101.5, 22.2, 95], [22.3, 91.5, 22.7, 88.5],
    [25.5, 80, 26.6, 74], [27.6, 69, 28.4, 66], [31, 59, 31.6, 52.5], [32.2, 50, 33.4, 50],
    [34.6, 56, 35.2, 62], [36.8, 72, 36.4, 78], [35.2, 84, 35.2, 90],
    [33.4, 101, 34.4, 118], [36, 131, 37.6, 139], [35.6, 147, 36.6, 158], [38, 170, 40.6, 181],
    [37.2, 187, 38.6, 192], [42, 195.5, 46.2, 193.5], [47.2, 190, 45.8, 181.5],
    [47, 168, 47, 156], [47, 148, 47.6, 140], [48.4, 124, 48.6, 112], [49.4, 106, 50, 104],
  ];
  let d = 'M50 3';
  pts.forEach(([cx, cy, x, y]) => { d += ` Q${cx} ${cy} ${x} ${y}`; });
  // Back up the right side: each curve ends where the previous one started.
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const [cx, cy] = pts[i];
    const [ex, ey] = i > 0 ? [pts[i - 1][2], pts[i - 1][3]] : [50, 3];
    d += ` Q${n(100 - cx)} ${cy} ${n(100 - ex)} ${ey}`;
  }
  return `${d} Z`;
}

const SILHOUETTE = silhouette();

const deltoids = pair([['M', 37.5, 33.8], ['Q', 28, 34.5, 25.6, 43.5], ['Q', 25, 49, 26.6, 51.5], ['Q', 31, 47, 33.6, 40.5], ['Q', 35, 36, 37.5, 33.8], ['Z']]);
const upperArms = pair([['M', 26.8, 52], ['Q', 23.8, 58, 24, 64], ['Q', 26.2, 67, 28.3, 65.5], ['Q', 31.2, 59, 31.4, 51.5], ['Q', 29, 50, 26.8, 52], ['Z']]);
const forearms = pair([['M', 22.7, 67.5], ['Q', 19.6, 75, 19, 86.5], ['L', 22.6, 87], ['Q', 26.2, 76, 28, 67.5], ['Q', 25.4, 68.6, 22.7, 67.5], ['Z']]);
const pecs = pair([['M', 49.6, 35], ['L', 38.4, 35], ['Q', 34.6, 40, 34, 47], ['Q', 36.4, 53.4, 42.4, 53.8], ['Q', 47.4, 53.8, 49.6, 51.2], ['Z']]);
const abs = [
  ...[[55, 63], [64.4, 72.4], [73.8, 81.8]].flatMap(([t, b]) => [rrect(43.6, t, 49.3, b, 2.5), rrect(50.7, t, 56.4, b, 2.5)]),
  ...pair([['M', 43.6, 83.2], ['L', 49.3, 83.2], ['L', 49.3, 96], ['Q', 45, 92, 43.6, 83.2], ['Z']]),
];
const obliques = pair([['M', 42.6, 55], ['L', 35.4, 60], ['Q', 37, 72, 36.6, 81], ['Q', 39.6, 86, 42.6, 89], ['Z']]);
const quads = pair([['M', 36, 92.5], ['Q', 33.8, 108, 35.6, 123], ['Q', 37.2, 132, 39.2, 136], ['L', 46.6, 136], ['Q', 48.4, 121, 48.2, 109], ['Q', 45.6, 100, 41.4, 96], ['Q', 38.6, 93.4, 36, 92.5], ['Z']]);
const shins = pair([['M', 38.2, 146], ['Q', 36.6, 158, 38.8, 171], ['L', 41.4, 179], ['L', 45.4, 179], ['Q', 46.6, 160, 46.8, 146], ['Q', 42.5, 148, 38.2, 146], ['Z']]);
const traps = 'M50 26.5 Q46 30.5 37.5 34 Q44.5 40 50 53 Q55.5 40 62.5 34 Q54 30.5 50 26.5 Z';
const lats = pair([['M', 38.4, 37.5], ['Q', 34, 44, 34.4, 52], ['Q', 35.6, 66, 40.2, 75], ['Q', 45.4, 76, 49.4, 72.5], ['L', 49.4, 55.5], ['Q', 44, 44, 38.4, 37.5], ['Z']]);
const lowerBackSteps = [['M', 40.4, 76.2], ['Q', 45.4, 77.4, 49.4, 73.6], ['L', 49.4, 88.4], ['Q', 43.6, 88.6, 36.4, 86.6], ['Q', 37.6, 80.4, 40.4, 76.2], ['Z']];
const lowerBack = pair(lowerBackSteps);
// One outline across both halves so the injury pulse draws a single ring.
const lowerBackWhole = 'M40.4 76.2 Q45.4 77.4 50 73.2 Q54.6 77.4 59.6 76.2 Q62.4 80.4 63.6 86.6 Q56.4 88.6 50 88.4 Q43.6 88.6 36.4 86.6 Q37.6 80.4 40.4 76.2 Z';
const glutes = pair([['M', 36.2, 88], ['Q', 34.4, 96, 35.8, 104], ['Q', 42.6, 109, 49.6, 105.6], ['L', 49.6, 89.6], ['Q', 43, 89.6, 36.2, 88], ['Z']]);
const hamstrings = pair([['M', 35.6, 106], ['Q', 34, 121, 36.6, 134.5], ['L', 47.2, 134.5], ['Q', 48.6, 121, 48.6, 108], ['Q', 42.4, 110, 35.6, 106], ['Z']]);
const calves = pair([['M', 37.6, 146], ['Q', 35.4, 155, 37.2, 165], ['Q', 41.6, 169, 46.6, 164], ['Q', 47.6, 154, 47, 146], ['Q', 42.3, 148.5, 37.6, 146], ['Z']]);
const neck = 'M45.6 25.5 L54.4 25.5 Q54.6 30 56.5 32.4 L43.5 32.4 Q45.4 30 45.6 25.5 Z';

const both = (paths) => ({ front: paths, back: paths });

const GEOMETRY = {
  focus: {
    chest: { front: pecs, back: [] },
    core: { front: [...abs, ...obliques], back: [] },
    shoulders: both(deltoids),
    arms: both([...upperArms, ...forearms]),
    back: { front: [], back: [traps, ...lats, ...lowerBack] },
    glutes: { front: [], back: glutes },
    legs: { front: [...quads, ...shins], back: [...hamstrings, ...calves] },
  },
  injury: {
    neck: both([neck]),
    shoulder: both(deltoids),
    elbow: both(ovalPair(26.2, 66.4, 7, 6.4)),
    wrist: both(ovalPair(20.6, 88, 6.4, 4.8)),
    lower_back: { front: [], back: [lowerBackWhole] },
    hip: { front: ovalPair(36.6, 91.5, 7.4, 9), back: [] },
    knee: both(ovalPair(42.6, 141, 9, 8.4)),
    ankle: both(ovalPair(43.2, 181.5, 7.4, 5.4)),
  },
};

const MUSCLE_LINES = {
  front: Object.values(GEOMETRY.focus).flatMap((r) => r.front),
  back: Object.values(GEOMETRY.focus).flatMap((r) => r.back),
};

/**
 * @param mode 'focus' (muscles, orange→red) or 'injury' (joints, red + pulse)
 * @param selected array of region keys
 * @param onToggle (key) => void
 * @param regionLabel (key) => display name, used for a11y
 */
export default function BodyAnatomyMap({ mode, selected, onToggle, regionLabel, frontLabel, backLabel }) {
  const uid = useId().replace(/:/g, '');
  const injury = mode === 'injury';
  const regions = GEOMETRY[injury ? 'injury' : 'focus'];
  const grad = `gm-ait-grad-${uid}`;
  const glow = `gm-ait-glow-${uid}`;

  const onKey = (e, key) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(key);
    }
  };

  const side = (which) => (
    <g transform={which === 'back' ? `translate(${BACK} 0)` : undefined}>
      <path d={SILHOUETTE} className="gm-ait-body" />
      {MUSCLE_LINES[which].map((d) => (
        <path key={d} d={d} className={`gm-ait-line${injury ? ' faint' : ''}`} />
      ))}
      {Object.entries(regions).map(([key, r]) => {
        const on = selected.includes(key);
        return r[which].map((d, i) => (
          <g
            // eslint-disable-next-line react/no-array-index-key
            key={`${key}-${i}`}
            className={`gm-ait-region${on ? ' on' : ''}${injury ? ' joint' : ''}`}
            role="button"
            tabIndex={i === 0 && which === (r.front.length ? 'front' : 'back') ? 0 : -1}
            aria-pressed={on}
            aria-label={regionLabel(key)}
            onClick={() => onToggle(key)}
            onKeyDown={(e) => onKey(e, key)}
          >
            <title>{regionLabel(key)}</title>
            {injury && on && <path d={d} className="gm-ait-pulse" />}
            <path d={d} className="gm-ait-glow" filter={`url(#${glow})`} />
            <path d={d} className="gm-ait-fill" fill={`url(#${grad})`} />
            <path d={d} className="gm-ait-edge" />
            <path d={d} className="gm-ait-hit" />
          </g>
        ));
      })}
      <path d={SILHOUETTE} className="gm-ait-outline" />
    </g>
  );

  return (
    <svg
      className={`gm-ait-map${injury ? ' injury' : ''}`}
      viewBox={`0 0 ${W} ${H + LABEL_SPACE}`}
      role="group"
    >
      <defs>
        <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={injury ? '#FF453A' : '#FF9F0A'} />
          <stop offset="1" stopColor={injury ? '#C6001E' : '#FF3B30'} />
        </linearGradient>
        <filter id={glow} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>
      {side('front')}
      {side('back')}
      <text x={50} y={H + 10} className="gm-ait-label">{frontLabel}</text>
      <text x={BACK + 50} y={H + 10} className="gm-ait-label">{backLabel}</text>
    </svg>
  );
}
